// Combinable filter-chip row, modeled on the Coffre's beloved filters. Within a
// group the parent ORs the selected keys; across groups it ANDs them. Purely
// presentational: the parent owns the active Set and does the filtering. Reused
// by Alignment (and available to any tab that wants the same affordance).

import { Fragment, type ReactNode } from 'react';

export interface Chip {
  key: string;
  label: ReactNode;
  count?: number;
  activeCls?: string; // custom colors (e.g. tier chips); falls back to a neutral highlight
  idleCls?: string;
  disabled?: boolean;
}

const DEFAULT_ACTIVE = 'border-ink/40 text-ink bg-white/[0.05]';
const DEFAULT_IDLE = 'border-ink/15 text-ink/45 hover:text-ink';

export function FilterChips({ groups, active, onToggle, onClear, allLabel = 'tous', allCount, className = '' }: {
  groups: Chip[][];          // each inner array is a group; a divider separates groups
  active: Set<string>;
  onToggle: (key: string) => void;
  onClear: () => void;
  allLabel?: string;
  allCount?: number;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`}>
      <button onClick={onClear}
        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
          active.size === 0 ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/55 hover:text-ink'
        }`}>
        {allLabel}{allCount != null ? ` ${allCount}` : ''}
      </button>
      {groups.filter((g) => g.length > 0).map((group, gi) => (
        <Fragment key={gi}>
          {gi > 0 && <span className="w-px h-4 bg-ink/10 mx-0.5" />}
          {group.map((c) => {
            const on = active.has(c.key);
            return (
              <button key={c.key} onClick={() => onToggle(c.key)} disabled={c.disabled}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                  on ? (c.activeCls ?? DEFAULT_ACTIVE) : (c.idleCls ?? DEFAULT_IDLE)
                } ${c.disabled ? 'opacity-30 cursor-not-allowed' : ''}`}>
                {c.label}{c.count != null ? ` ${c.count}` : ''}
              </button>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
